import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { splitFullName } from '../lib/nameUtils';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { toast } from 'sonner';
import { BadgeCheck, ShieldQuestion, UserRound, Loader2, UserRoundPlus, Mail, Phone } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Hover a person's name anywhere → shows the (verified) contact photo + basics.
// Click → opens the contact on the Contacts page (creates it first with canCreate).
export function ContactHoverCard({ name, email = '', company = '', projectId = '', canCreate = false, contactType = 'Owner', children }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: false, loaded: false, contact: null });
  const [creating, setCreating] = useState(false);
  const [opening, setOpening] = useState(false);

  const load = (open) => {
    if (!open || state.loaded || state.loading) return;
    setState(s => ({ ...s, loading: true }));
    api.matchContact(name, email, company)
      .then(r => setState({ loading: false, loaded: true, contact: r.data.found ? r.data.contact : null }))
      .catch(() => setState({ loading: false, loaded: true, contact: null }));
  };

  const doCreate = async (force = false) => {
    const { first, last } = splitFullName(name);
    if (!last) return null;
    const body = {
      first_name: first,
      last_name: last,
      company, email, type: contactType,
      project_ids: projectId ? [projectId] : [],
      ...(force ? { force: true } : {}),
    };
    const r = await api.createContact(body);
    setState({ loading: false, loaded: true, contact: { ...r.data, has_photo: false } });
    return r.data;
  };

  const createContact = async (force = false) => {
    setCreating(true);
    try {
      const c = await doCreate(force);
      if (c) toast.success('Contact created — add a photo on the Contacts page');
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.matches) {
        toast.warning(d.message, { duration: 10000, action: { label: 'Trotzdem anlegen', onClick: () => createContact(true) } });
      } else toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to create contact');
    }
    setCreating(false);
  };

  const openContact = async () => {
    if (opening) return;
    setOpening(true);
    try {
      let c = state.contact;
      if (!state.loaded) {
        const r = await api.matchContact(name, email, company);
        c = r.data.found ? r.data.contact : null;
        setState({ loading: false, loaded: true, contact: c });
      }
      if (!c && canCreate) {
        c = await doCreate();
        if (c) toast.success('Contact created — opening the contact page');
      }
      if (c?.id) navigate(`/contacts?edit=${c.id}`);
      else toast.info(`No CRM contact for "${name}" yet`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.matches?.length) {
        toast.warning(d.message, { duration: 10000, action: { label: 'Bestehenden öffnen', onClick: () => navigate(`/contacts?edit=${d.matches[0].id}`) } });
      } else toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to open contact');
    }
    setOpening(false);
  };

  const c = state.contact;
  return (
    <HoverCard openDelay={250} onOpenChange={load}>
      <HoverCardTrigger asChild>
        {React.cloneElement(children, { onClick: openContact })}
      </HoverCardTrigger>
      <HoverCardContent className="bg-card border-border rounded-sm w-64 p-3 z-[120]" data-testid="contact-hover-card">
        {state.loading || !state.loaded ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading contact…
          </div>
        ) : !c ? (
          <div className="space-y-2 py-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <UserRound className="w-4 h-4" /> No contact found for &ldquo;{name}&rdquo;
            </div>
            {canCreate && (
              <button onClick={createContact} disabled={creating} data-testid="hover-create-contact-btn"
                className="flex items-center gap-1.5 text-[11px] text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserRoundPlus className="w-3 h-3" />}
                Create contact{company ? ` (${company})` : ''}
              </button>
            )}
          </div>
        ) : (
          <div className="flex gap-3">
            <div className="w-16 h-16 rounded-sm overflow-hidden bg-secondary/50 border border-border shrink-0 flex items-center justify-center">
              {c.has_photo ? (
                <img src={`${API_BASE}/api/contacts/${c.id}/photo/file`} alt={c.last_name}
                  className="w-full h-full object-cover" data-testid="contact-hover-photo" />
              ) : (
                <UserRound className="w-6 h-6 text-muted-foreground/40" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-foreground font-medium truncate">
                {[c.title, c.first_name, c.last_name].filter(Boolean).join(' ')}
              </p>
              {c.company && <p className="text-xs text-muted-foreground truncate">{c.company}</p>}
              {c.type && <span className="inline-block text-[9px] font-mono text-violet-300 border border-violet-600/40 bg-violet-600/10 rounded px-1.5 py-0.5 mt-1">{c.type}</span>}
              {(c.email || c.email_private) && (
                <a href={`mailto:${c.email || c.email_private}`} data-testid="contact-hover-email"
                  className="flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 mt-1 truncate">
                  <Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{c.email || c.email_private}</span>
                </a>
              )}
              {[['phone_work', 'work'], ['phone_private', 'priv'], ['mobile', 'mobil']].map(([f, tag]) => c[f] ? (
                <a key={f} href={`tel:${c[f]}`} data-testid={`contact-hover-${f}`}
                  className="flex items-center gap-1 text-[10px] text-foreground/80 hover:text-foreground mt-0.5">
                  <Phone className="w-3 h-3 shrink-0" /> {c[f]} <span className="text-muted-foreground/60">({tag})</span>
                </a>
              ) : null)}
              {c.has_photo && (
                <p className={`flex items-center gap-1 text-[10px] mt-1 ${c.photo_verified ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {c.photo_verified
                    ? <><BadgeCheck className="w-3 h-3" /> Verified{c.photo_verified_by ? ` by ${c.photo_verified_by}` : ''}</>
                    : <><ShieldQuestion className="w-3 h-3" /> Photo not verified yet</>}
                </p>
              )}
            </div>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
